import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useRoutes } from 'react-router-dom';
import SuspenseLoader from './components/SuspenseLoader';
import { useAppDispatch, useAppSelector } from './redux/root';
import { onDeleteAdmins, onSetAdmins } from './redux/slices/admins.slice';
import { onDeleteAlQuran, onSetAlQuran } from './redux/slices/al-quran.slice';
import { onDeleteAzkaar, onSetAzkaar } from './redux/slices/azkaar.slice';
import { onDeleteCampange, onSetCampange } from './redux/slices/campange.slice';
import { onDeleteContacts, onSetContacts } from './redux/slices/contacts.slice';
import { onGetContent } from './redux/slices/content.slice';
import { onSetCotisation } from './redux/slices/cotisation.slice';
import { onDeleteKhassida, onSetKhassida } from './redux/slices/khassida.slice';
import { onDeletePlaylist, onSetPlaylist } from './redux/slices/playlist.slice';
import { onDeleteSalatou, onSetSalatou } from './redux/slices/salatou.slice';
import { onLogout, onSetUser, onSetUserInfos } from './redux/slices/user.slice';
import routes from './router';
import ThemeProvider from './theme/ThemeProvider';
import { authListener, logoutAsync } from './utils/api/auth.api';
import { useCotisationListener } from './utils/api/hooks';
import {
  alQuranListener,
  azkaarListener,
  campangeListener,
  contactsListener,
  contentListner,
  khassidaListener,
  playlistListener,
  salatousListener,
  userListener,
  usersListener
} from './utils/api/user.api';
const App = () => {
  const content = useRoutes(routes);
  const [loading, setLoading] = useState(true);

  const auth = useAppSelector((s) => s.auth);
  const userIdRef = useRef<string | undefined>(auth?.user?.uid);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const subs = authListener((u) => {
      if (u) {
        setLoading(true);
        userIdRef.current = u?.uid;
        const userData: any = _.pick(u, [
          'uid',
          'email',
          'emailVerified',
          'displayName',
          'phoneNumber',
          'photoURL'
        ]);
        dispatch(onSetUser(userData));
      } else {
        userIdRef.current = undefined;
        dispatch(onLogout());
        setLoading(false);
      }
    });
    return () => {
      subs();
    };
  }, []);

  useEffect(() => {
    if (userIdRef.current) {
      const listener = userListener(userIdRef.current, (data) => {
        if (data) {
          if (!data.isAdmin) {
            logoutAsync(() => null);
          } else {
            dispatch(onSetUserInfos(data));
          }
        }
        setLoading(false);
      });
      return () => listener();
    }
  }, [userIdRef.current]);

  useEffect(() => {
    if (userIdRef.current) {
      const listener1 = usersListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetAdmins(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteAdmins(deleteds));
        }
      });

      const listener2 = khassidaListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetKhassida(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteKhassida(deleteds));
        }
      });
      const listener3 = contactsListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetContacts(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteContacts(deleteds));
        }
      });

      const listener4 = alQuranListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetAlQuran(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteAlQuran(deleteds));
        }
      });

      const listener5 = azkaarListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetAzkaar(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteAzkaar(deleteds));
        }
      });
      const listener6 = playlistListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetPlaylist(data));
        }
        if (deleteds.length) {
          dispatch(onDeletePlaylist(deleteds));
        }
      });

      const listener7 = salatousListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetSalatou(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteSalatou(deleteds));
        }
      });

      const listener8 = campangeListener((data, deleteds) => {
        if (data.length) {
          dispatch(onSetCampange(data));
        }
        if (deleteds.length) {
          dispatch(onDeleteCampange(deleteds));
        }
      });

      return () => {
        listener1();
        listener2();
        listener3();
        listener4();
        listener5();
        listener6();
        listener7();
        listener8();
      };
    }
  }, [userIdRef.current]);

  useEffect(() => {
    const listener8 = contentListner((data) => {
      dispatch(onGetContent(data));
    });

    return () => {
      listener8();
    };
  }, []);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        {!loading && content}
        {loading && <SuspenseLoader />}
        {!!userIdRef?.current && <CotisationListner />}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
export default App;

function CotisationListner() {
  const dispatch = useAppDispatch();
  const [startDate] = useState(moment().startOf('months').format('YYYY-MM-DD'));

  const { data } = useCotisationListener('month', startDate);
  useEffect(() => {
    dispatch(onSetCotisation(data));
  }, [data]);

  return null;
}
